import { useEffect, useState } from "react";
import { Check, Copy, Link2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type Props = {
  affiliateId: string;
  referralCode: string | null;
};

const SITE = "https://emrkt.co";

export default function ReferralLinkCard({ affiliateId, referralCode }: Props) {
  const [dest, setDest] = useState("/");
  const [copied, setCopied] = useState(false);
  const [clickCount, setClickCount] = useState<number | null>(null);
  const [last30, setLast30] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const total = await supabase
        .from("affiliate_clicks")
        .select("id", { count: "exact", head: true })
        .eq("affiliate_id", affiliateId);
      const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const recent = await supabase
        .from("affiliate_clicks")
        .select("id", { count: "exact", head: true })
        .eq("affiliate_id", affiliateId)
        .gte("created_at", since);
      if (cancelled) return;
      setClickCount(total.count ?? 0);
      setLast30(recent.count ?? 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [affiliateId]);

  if (!referralCode) {
    return (
      <Card className="p-5 text-sm text-muted-foreground">
        Your referral code hasn't been generated yet. Reach out to support and we'll provision one.
      </Card>
    );
  }

  const safeDest = dest.startsWith("/") && !dest.startsWith("//") ? dest : "/";
  const url = `${SITE}/r/${referralCode}${safeDest === "/" ? "" : `?to=${encodeURIComponent(safeDest)}`}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <Card className="p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Link2 className="h-4 w-4 text-primary" />
        <h3 className="font-display text-base font-semibold">Your referral link</h3>
      </div>
      <p className="text-sm text-muted-foreground">
        Share this link anywhere — emails, social, your site. New signups and conversions get
        attributed to you for 90 days.
      </p>

      <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-end">
        <div>
          <Label htmlFor="ref-dest" className="text-xs uppercase tracking-wider text-muted-foreground">
            Send people to
          </Label>
          <Input
            id="ref-dest"
            value={dest}
            onChange={(e) => setDest(e.target.value)}
            placeholder="/pricing"
            className="mt-1"
          />
        </div>
        <Button variant="outline" size="sm" onClick={copy} className="h-10">
          {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
          {copied ? "Copied" : "Copy link"}
        </Button>
      </div>

      <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs font-mono break-all">
        {url}
      </div>

      <div className="grid grid-cols-2 gap-4 pt-2">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">All-time clicks</div>
          <div className="font-display text-2xl font-bold mt-1">{clickCount ?? "—"}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Last 30 days</div>
          <div className="font-display text-2xl font-bold mt-1">{last30 ?? "—"}</div>
        </div>
      </div>
    </Card>
  );
}